import { useAvailableDimensionsByDataSourceV2 } from "@/hooks/useAvailableDimensionsByDataSource";
import useAvailableMeasuresByDataSource from "@/hooks/useAvailableMeasuresByDataSource";
import { focusDimensionSchemaNameConverter } from "@/utils/focus/focusDimensionSchemaNameConverter";
import { translateBillingVendorToFocusProvider } from "@/utils/focus/focusProviderConverter";
import { useQuery } from "@tanstack/react-query";
import { DataSource } from "@ternary/api-lib/analytics/enums";
import { keyBy } from "lodash";
import CoreAPIClient from "../../../api/core/CoreAPIClient";
import CoreAPIClientError from "../../../api/core/CoreAPIClientError";
import { useCoreAPIClient } from "../../../api/core/CoreAPIClientProvider";
import useGatekeeper from "../../../hooks/useGatekeeper";
import { UseQueryOptions, UseQueryResult } from "../../../lib/react-query";
import copyText from "../copyText";
import { getFocusMeasureForBudget } from "../utils";

type BudgetEntity = Awaited<
  ReturnType<CoreAPIClient["getBudgetsByTenantID"]>
>[number];

const BILLING_VENDOR_DIMENSION_KEY = "vendor";

export default function useGetBudgetsByTenantID(
  tenantID: string,
  options?: UseQueryOptions<BudgetEntity[], CoreAPIClientError>
): UseQueryResult<BudgetEntity[], CoreAPIClientError> {
  const client = useCoreAPIClient();
  const gatekeeper = useGatekeeper();

  // Required for mapping legacy budgets with billing dimensions
  // TODO: Migrate Budgets to FOCUS so we don't have to do this conversion in the ui
  const allDimensions = useAvailableDimensionsByDataSourceV2(
    DataSource.FOCUS_BILLING
  );

  const allDimensionsKeyedBySchemaName = keyBy(allDimensions, "schemaName");

  const availableMeasures = useAvailableMeasuresByDataSource(
    DataSource.FOCUS_BILLING
  );

  const availableMeasuresKeyedBySchemaName = keyBy(
    availableMeasures,
    "schemaName"
  );

  return useQuery({
    queryKey: ["budgets", tenantID],
    queryFn: async () => {
      const budgets = await client.getBudgetsByTenantID(tenantID);

      return budgets.map((budget) => ({
        ...budget,
        measure: getFocusMeasureForBudget(
          budget.measure,
          availableMeasuresKeyedBySchemaName
        ),
        scopes: budget.scopes.map((scope) => ({
          values: scope.values.map((value) =>
            scope.key === BILLING_VENDOR_DIMENSION_KEY
              ? translateBillingVendorToFocusProvider(value)
              : value
          ),
          key: focusDimensionSchemaNameConverter(
            scope.key,
            allDimensionsKeyedBySchemaName
          ),
        })),
      }));
    },
    enabled: gatekeeper.canListBudgets,
    meta: { errorMessage: copyText.errorLoadingBudgetsMessage },
    ...options,
  });
}
